import React from 'react';
import { motion } from 'framer-motion';

const WhyUs: React.FC = () => {
  const values = [
    {
      title: "Premium from First Click to Final Frame",
      description: "Your audience will feel the difference, before they even read a word."
    },
    {
      title: " Built for Legacy",
      description: "We don't do \"one and done.\" Our work is designed to grow with you, to evolve, scale, and endure beyond trends."
    },
    {
      title: "Illusions with Intent",
      description: "We translate your brand's essence into an experience people feel, not just see."
    },
    {
      title: "End-to-End Elegance",
      description: "From visuals to voice, strategy to software, every detail is curated to feel seamless, premium, and high-touch."
    },
    {
      title: "Vision-Driven Excellence",
      description: "We don't just make things look good. We make them feel inevitable, as if they always belonged exactly where they are."
    },
    {
      title: "Unmistakably You",
      description: "We work with creators and businesses that are serious about growth. Our work doesn't look like \"agency work.\" It looks like you, refined, elevated, and impossible to replicate."
    }
  ];

  return (
    <section id="why-us" className="relative py-20 px-6" style={{ backgroundColor: '#fefdf8' }}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Subtle baroque flourish pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M200 400C200 300 300 200 400 200C500 200 600 300 600 400C600 500 500 600 400 600C300 600 200 500 200 400Z" stroke="#d4af37" strokeWidth="2" fill="none" opacity="0.3"/>
            <path d="M800 300C800 250 850 200 900 200C950 200 1000 250 1000 300C1000 350 950 400 900 400C850 400 800 350 800 300Z" stroke="#d4af37" strokeWidth="1.5" fill="none" opacity="0.2"/>
            <path d="M100 600C150 550 200 600 250 550C300 600 350 550 400 600" stroke="#d4af37" strokeWidth="1" fill="none" opacity="0.15"/>
            <path d="M700 150C750 100 800 150 850 100C900 150 950 100 1000 150" stroke="#d4af37" strokeWidth="1" fill="none" opacity="0.15"/>
          </svg>
        </div>
        
        {/* Holographic globe mesh effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full border border-amber-400/30 animate-pulse"></div>
          <div className="absolute top-20 right-20 w-24 h-24 rounded-full border border-amber-400/20 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/4 w-20 h-20 rounded-full border border-amber-400/25 animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-10 right-1/3 w-28 h-28 rounded-full border border-amber-400/15 animate-pulse" style={{ animationDelay: '3s' }}></div>
        </div>

        {/* Geometric pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `linear-gradient(30deg, transparent 40%, rgba(212, 175, 55, 0.1) 50%, transparent 60%),
                             linear-gradient(150deg, transparent 40%, rgba(212, 175, 55, 0.1) 50%, transparent 60%)`
          }}></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <motion.h2 
            className="text-5xl font-serif text-amber-700 mb-4 relative"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Why Manifest Illusions
            <motion.div 
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 h-0.5 bg-gradient-to-r from-transparent via-amber-600 to-transparent"
              initial={{ width: 0 }}
              whileInView={{ width: '200px' }}
              transition={{ duration: 1.2, delay: 0.5 }}
            />
          </motion.h2>
          <p className="text-xl text-gray-600 mb-8 font-light">
            One brand. Everything you need. 
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <p className="text-gray-700 mb-6 leading-relaxed font-light">
          At Manifest Illusions, we don't just design brands, we engineer presence. Every asset we craft is built with precision, intention, and performance in mind. From elevated product visuals to meticulously planned conversion campaigns, our work isn't about trends, it's about timeless impact.
          </p>
          
          <p className="text-gray-700 mb-6 leading-relaxed font-light">
            Whether you're launching a new store or repositioning your brand for greater market authority, we combine intelligent design, strategic clarity, and psychological insight to ensure your brand doesn't just fit in, it leads.
          </p>
          
          <p className="text-gray-700 mb-8 leading-relaxed font-light">
You don't need a roster of freelancers or an overpriced agency wrapped in buzzwords. You need a firm that understands luxury through execution. One team. One vision. Every detail, considered.
          </p>
          
          <div className="text-center">
            <h3 className="text-2xl font-serif text-amber-700 mb-4">
              Become Unforgettable In A World That Forgets Everything.
            </h3>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white/70 backdrop-blur-lg p-8 rounded-lg border border-amber-200/50 hover:border-amber-400/70 hover:shadow-lg transition-all duration-300 relative overflow-hidden group"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                boxShadow: '0 4px 20px rgba(212, 175, 55, 0.1)'
              }}
            >
              {/* Hover glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-100/20 to-amber-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="relative z-10">
                <h3 className="text-lg font-serif text-amber-800 mb-4 tracking-wide uppercase text-sm font-medium group-hover:text-amber-900 transition-colors duration-300 relative">
                  {value.title}
                  <span className="absolute bottom-0 left-0 w-0 h-px bg-amber-600 group-hover:w-full transition-all duration-300"></span>
                </h3>
                <div className="w-12 h-px bg-gradient-to-r from-amber-400 to-transparent mb-4"></div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">{value.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Section Outro */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-600 font-serif italic">
            "Your brand. Refined. Remembered."
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUs;